import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, GameController } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { gameSystems } from '../data/gameSystems';
import { useApp } from '../context/AppContext';
import { useTranslation } from '../hooks/useTranslation';
import { toast } from 'sonner';

export const CharacterModal = ({ isOpen, onClose, editCharacter = null }) => {
  const { dispatch } = useApp();
  const { t, lang } = useTranslation();
  const [name, setName] = useState('');
  const [systemId, setSystemId] = useState('');
  
  const isEditing = !!editCharacter;
  
  useEffect(() => {
    if (editCharacter) {
      setName(editCharacter.name);
      setSystemId(editCharacter.systemId);
    } else {
      setName('');
      setSystemId('');
    }
  }, [editCharacter, isOpen]);
  
  const handleSubmit = () => {
    if (!name.trim() || !systemId) return;
    
    if (isEditing) {
      dispatch({
        type: 'UPDATE_CHARACTER',
        payload: { id: editCharacter.id, updates: { name: name.trim() } }
      });
      toast.success(t('save'));
    } else {
      const trimmedName = name.trim();
      dispatch({
        type: 'CREATE_CHARACTER',
        payload: { name: trimmedName, systemId }
      });
      toast.success(t('characterCreated', { name: trimmedName }));
    }
    
    handleClose();
  };
  
  const handleClose = () => {
    setName('');
    setSystemId('');
    onClose();
  };
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          
          {/* Modal */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--surface)] rounded-t-3xl p-6 max-w-md mx-auto"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[var(--primary)]/10 flex items-center justify-center">
                  <User size={24} weight="fill" className="text-[var(--primary)]" />
                </div>
                <h2 className="text-xl font-bold text-[var(--text)]">
                  {isEditing ? t('editCharacter') : t('createCharacter')}
                </h2>
              </div>
              <button 
                onClick={handleClose}
                className="p-2 rounded-full hover:bg-[var(--background)] transition-colors"
                data-testid="close-character-modal"
              >
                <X size={24} className="text-[var(--text-muted)]" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                  {t('characterName')}
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('characterName')}
                  className="h-14 bg-[var(--background)] border-[var(--border)] text-[var(--text)]"
                  data-testid="character-name-input"
                />
              </div>
              
              {/* System - only show for new characters */}
              {!isEditing && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-muted)] mb-2">
                    {t('selectSystem')}
                  </label>
                  <Select value={systemId} onValueChange={setSystemId}>
                    <SelectTrigger 
                      className="h-14 bg-[var(--background)] border-[var(--border)] text-[var(--text)]"
                      data-testid="system-select-trigger"
                    >
                      <SelectValue placeholder={t('selectSystem')} />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
                      {Object.values(gameSystems).map(system => (
                        <SelectItem 
                          key={system.id} 
                          value={system.id}
                          className="text-[var(--text)]"
                          data-testid={`system-option-${system.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <GameController size={20} weight="fill" />
                            {system.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Submit */}
              <Button
                onClick={handleSubmit}
                disabled={!name.trim() || (!isEditing && !systemId)}
                className="w-full h-14 rounded-2xl text-lg font-semibold bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white transition-all"
                data-testid="save-character-btn"
              >
                {isEditing ? t('save') : t('createCharacter')}
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
